import React, { useCallback, useState } from 'react';
import Dialog from '../../../../../../../../../../../BaseComponents/Dialog/EDDialog';
import { EDTextEdit } from '../../../../../../../../../../../BaseComponents/TextEdit/EDTextEdit';
import { ILocalAnalys } from '../../../../../EkonomiskAnalysMain/EkonomiskAnalysMain';
import { localAnalysToTedEkAnalys } from '../../../../../../Utils/convertUtils';
import EDFormGroup from '../../../../../../../../../../../BaseComponents/FormGroup/EDFormGroup';
import EDGrid from '../../../../../../../../../../../BaseComponents/GridComponents/EDGrid';
import EDCheckbox from '../../../../../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import { clone } from 'ramda';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  ISelectedAnalysAnalys,
  ISelectedAnalysCodePrivilege,
  ISelectedAnalys,
} from '../../../../../../EkonomiskAnalysPage';
import { SaveFavoriteAnalysFnType } from '../../../../EkonomiskAnalysSelect/Components/FavoriteAnalysDialog/Queries/ChangeAnalysFavoriteMutation/ChangeAnalysFavoriteMutation';
import AnalysEgnaLokalaQuery from '../../Queries/AnalysEgnaLokalaQuery';
import StandardAnalysQuery from '../../Queries/StandardAnalysQuery';
import EDOverlay from '../../../../../../../../../../../BaseComponents/Overlay/EDOverlay';
import EDKeybind from '../../../../../../../../../../../BaseComponents/Keybinds/EDKeybind';
import { equals } from 'ramda';
import { SaveAnalysVariables } from '../../Mutations/SaveAnalysMutation/_QueryTypes/SaveAnalys';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import { SaveAnalysCallbackType } from '../../Mutations/SaveAnalysMutation/SaveAnalysMutation';
import { IUserInfo } from '../../../../../../Utils/ekonomiskAnalysInitQueryUtils';
import EDTypography from '../../../../../../../../../../../BaseComponents/Typography/EDTypography';
import EkonomiskAnalysUserSignaturesQuery from '../../Queries/EkonomiskAnalysUserSignaturesQuery';
import { EDAutoCompleteEdit } from '../../../../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { ISelectChoice } from '../../../../../../../../../../../BaseComponents/Selects/SelectCommons';
import { urvalHasValue } from '../../../../../../../../../../../Components/Urval/Utils/urvalUtils';
import EDIcon from '../../../../../../../../../../../BaseComponents/Icon/EDIcon';
import mergeSort from '../../../../../../../../../../../Utils/mergeSort';
import EDAvenyProgress from '../../../../../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDChip from '../../../../../../../../../../../BaseComponents/Chip/EDChip';
import EDBox from '../../../../../../../../../../../BaseComponents/EDBox/EDBox';

interface IProps {
  open: boolean;
  toggleOpen: () => void;
  analysSettings: ILocalAnalys;
  selectedAnalys: ISelectedAnalys;
  dbName: string;
  saveAnalys: (
    mutationFn: SaveAnalysVariables,
    callback?: SaveAnalysCallbackType
  ) => void;
  changeFavoriteAnalys: SaveFavoriteAnalysFnType;
  selectAnalys: (
    analys: ISelectedAnalysAnalys,
    codePrivilege: ISelectedAnalysCodePrivilege,
    sammanfattningsMode: boolean
  ) => void;
  saving: boolean;
  toggleSaving: () => void;
  newAnalys: boolean;
  userInfo?: IUserInfo;
}

export enum AnalysDistribution {
  Egen = 'Egna lokala',
  Gemensam = 'Gemensamma lokala',
  SparaForAnvandare = 'Spara för användare',
}

const ANVANDS_FOR_LIST: string[] = [
  'Balans',
  'Resultat',
  'Blandat',
  'Hela huvudboken',
];

const addHelaHuvudboken = (originalAnvandsFor: string[]): string[] => {
  const value = (ANVANDS_FOR_LIST.indexOf('Hela huvudboken') + 1).toString();
  return [...originalAnvandsFor.filter(s => s !== value), value];
};

const initialAnvandsFor = (
  originalAnvandsFor: string[],
  codePrivilegeAnvandsFor?: string | null
): string[] => {
  let anvandsFor: string[] = [];
  if (!originalAnvandsFor.length && codePrivilegeAnvandsFor != null) {
    anvandsFor = [codePrivilegeAnvandsFor];
  } else {
    anvandsFor = originalAnvandsFor;
  }

  return addHelaHuvudboken(anvandsFor);
};

const getAnvandsForUrvalConstants = (anvandsFor: string[]) => {
  return anvandsFor.map(value => {
    switch (value) {
      case '1':
        return 'BALANS';
      case '2':
        return 'RESULTAT';
      case '3':
        return 'BLANDAT';
      case '4':
        return 'HELA_HUVUDBOKEN';
      default:
        return '';
    }
  });
};

const getAnalysNameError = (
  usedNames: string[],
  name: string
): string | undefined => {
  const usedAnalysName = usedNames.includes(name.trim().toLowerCase());
  return usedAnalysName ? 'En analys med namnet finns redan' : undefined;
};

const useStyles = makeStyles()((theme: Theme) => ({
  checkboxContainer: {
    marginTop: theme.spacing(3),
  },
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"]': {
      paddingTop: theme.spacing(0.5),
    },
  },
  savingOverlay: {
    right: 'calc(50% - 20px)',
    bottom: 'calc(50% - 20px)',
  },
  tag: {
    backgroundColor: theme.palette.grey[300],
    height: 'auto',
    margin: `${theme.spacing(0.5)} ${theme.spacing(0.25)}`,
    minHeight: theme.spacing(3),
  },
  warningContainer: {
    display: 'flex',
    flex: '0 1 auto',
    alignItems: 'center',
  },
  warningColor: {
    color: theme.palette.warning.main,
  },
}));

const getDistribution = (
  value?: string,
  canSaveGemensamma?: boolean
): AnalysDistribution => {
  if (
    canSaveGemensamma &&
    value &&
    Object.values(AnalysDistribution).includes(value as AnalysDistribution)
  ) {
    return value as AnalysDistribution;
  }

  return AnalysDistribution.Egen;
};

/**
 * En dialog där en analys kan modifieras, eller inställningar för att spara ner en ny analys kan göras.
 */
export const EkonomiskAnalysSaveAnalysModal: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { toggleOpen, changeFavoriteAnalys, selectAnalys, saveAnalys } = props;
  const { classes } = useStyles();

  const [anvandsForValue, setAnvandsForValue] = useState(
    initialAnvandsFor(
      props.analysSettings.anvandsFor,
      props.selectedAnalys.codePrivilege.anvandsFor
    )
  );
  const [analysName, setAnalysName] = useState<string>(
    props.newAnalys ? '' : props.analysSettings.name
  );
  const [analysNameError, setAnalysNameError] = useState<string>();
  const [changedAnalysName, setChangedAnalysName] = useState<boolean>(false);
  const [changedCheckbox, setChangedCheckbox] = useState<boolean>(false);
  const [changedDist, setChangedDist] = useState<boolean>(false);
  const [analysNamePristine, setAnalysNamePristine] = useState<boolean>(true);
  const [distribution, setDistribution] = useState<AnalysDistribution>(
    getDistribution(
      props.analysSettings.category,
      props.userInfo?.isUtdelningBehorig ?? false
    )
  );
  const [sparaForAnvandare, setSparaForAnvandare] = useState<
    ISelectChoice<string>[]
  >([]);
  const [prevNewAnalys, setPrevNewAnalys] = useState(props.newAnalys);

  const [prevAnalysSettings, setPrevAnalysSettings] = useState(
    props.analysSettings
  );

  const [prevAnvandsFor, setPrevAnvandsFor] = useState<string[]>(
    initialAnvandsFor(
      props.analysSettings.anvandsFor,
      props.selectedAnalys.codePrivilege.anvandsFor
    )
  );

  const [prevCodePrivilegeAnvandsFor, setPrevCodePrivilegeAnvandsFor] =
    useState<string | null | undefined>(
      props.selectedAnalys.codePrivilege.anvandsFor
    );

  const resetStates = useCallback(() => {
    setAnvandsForValue(
      initialAnvandsFor(
        props.analysSettings.anvandsFor,
        props.selectedAnalys.codePrivilege.anvandsFor
      )
    );
    setAnalysName(props.newAnalys ? '' : props.analysSettings.name);
    setAnalysNameError(undefined);
    setChangedAnalysName(false);
    setChangedCheckbox(false);
    setChangedDist(false);
    setAnalysNamePristine(true);
    setDistribution(
      getDistribution(
        props.analysSettings.category,
        props.userInfo?.isUtdelningBehorig ?? false
      )
    );
    setSparaForAnvandare([]);
    setPrevNewAnalys(props.newAnalys);
    setPrevAnalysSettings(props.analysSettings);
    setPrevAnvandsFor(props.analysSettings.anvandsFor);
    setPrevCodePrivilegeAnvandsFor(
      props.selectedAnalys.codePrivilege.anvandsFor
    );
  }, [
    props.analysSettings,
    props.newAnalys,
    props.selectedAnalys.codePrivilege.anvandsFor,
    props.userInfo?.isUtdelningBehorig,
  ]);

  const analysNameChange = (value: string, usedNames: string[]) => {
    setAnalysName(value);
    setAnalysNameError(getAnalysNameError(usedNames, value));
    setAnalysNamePristine(false);
    setChangedAnalysName(checkAnalysNamnChanged(value));
  };

  const distributionChange = (
    distribution: ISelectChoice<string> | null
  ) => {
    if (distribution !== null) {
      const origDistribution = getDistribution(
        props.analysSettings.category,
        props.userInfo?.isUtdelningBehorig ?? false
      );

      const selectedDistribution = distribution.value as AnalysDistribution;

      const changedDist = !equals(
        origDistribution.trim().toLowerCase(),
        selectedDistribution.trim().toLowerCase()
      );

      setDistribution(selectedDistribution);
      setChangedDist(changedDist);
    }
  };

  const sparaForAnvandareChange = (newAnvandare: ISelectChoice<string>[]) => {
    if (newAnvandare != null) {
      if (Array.isArray(newAnvandare)) {
        setSparaForAnvandare([...newAnvandare]);
      }
    }
  };

  const checkAnalysNamnChanged = useCallback(
    (analysName: string): boolean => {
      const origAnalysName = props.newAnalys ? '' : props.analysSettings.name;

      return (
        origAnalysName.trim().toLowerCase() !== analysName.trim().toLowerCase()
      );
    },
    [props.analysSettings.name, props.newAnalys]
  );

  const checkAnvandsForChanged = useCallback(
    (anvandsFor: string[]): boolean => {
      const initAnvandsFor = initialAnvandsFor(
        props.analysSettings.anvandsFor,
        props.selectedAnalys.codePrivilege.anvandsFor
      );

      return !equals(mergeSort(initAnvandsFor), mergeSort(anvandsFor));
    },
    [
      props.analysSettings.anvandsFor,
      props.selectedAnalys.codePrivilege.anvandsFor,
    ]
  );

  const anvandForCheck = (value: string) => {
    const anvandsFor = clone(anvandsForValue);
    const index = anvandsFor.indexOf(value);
    if (index === -1) {
      anvandsFor.push(value);
    } else {
      anvandsFor.splice(index, 1);
    }

    setAnvandsForValue(anvandsFor);
    setSparaForAnvandare([]);
    setChangedCheckbox(checkAnvandsForChanged(anvandsFor));
  };

  const checkCheckboxMarker = (value: string) => {
    const initAnvandsFor = initialAnvandsFor(
      props.analysSettings.anvandsFor,
      props.selectedAnalys.codePrivilege.anvandsFor
    );

    return (
      (!initAnvandsFor.includes(value) && anvandsForValue.includes(value)) ||
      (initAnvandsFor.includes(value) && !anvandsForValue.includes(value))
    );
  };

  const closeModal = useCallback(() => {
    toggleOpen();
  }, [toggleOpen]);

  const exitModal = () => {
    resetStates();
    if (props.saving) {
      props.toggleSaving();
    }
  };

  const saveAnalysCallback: SaveAnalysCallbackType = useCallback(
    (id, analysName, utdelningLopnr, grundurval) => {
      changeFavoriteAnalys(
        [
          {
            analysId: id,
            favoritLopnr: null,
            kodBehId: props.selectedAnalys.codePrivilege.id,
            utdelatLopnr: distribution === AnalysDistribution.Gemensam ? 0 : 1,
            saveForUserId:
              distribution === AnalysDistribution.SparaForAnvandare
                ? sparaForAnvandare?.map(option => option.value).join('|')
                : undefined,
          },
        ],
        () =>
          selectAnalys(
            {
              name: analysName,
              id,
              utdelningLopnr,
              grundurval,
            },
            props.selectedAnalys.codePrivilege,
            false
          )
      );
    },
    [
      distribution,
      changeFavoriteAnalys,
      selectAnalys,
      props.selectedAnalys.codePrivilege,
      sparaForAnvandare,
    ]
  );

  const handleSaveAnalys = useCallback(() => {
    if (props.open) {
      const analys: ILocalAnalys = {
        ...props.analysSettings,
        name: analysName,
        category: distribution,
        anvandsFor: anvandsForValue,
        title: analysName,
      };

      const { other } = analys;

      const tedAnalys = localAnalysToTedEkAnalys(
        analys,
        props.newAnalys ? undefined : props.selectedAnalys.analys.id
      );
      const saveForUserIDs = sparaForAnvandare
        ?.map(option => option.value)
        .join('|');

      saveAnalys(
        {
          analys: tedAnalys,
          dbname: props.dbName,
          saveForUserId: saveForUserIDs,
          new_analys: props.newAnalys,
          anv_urval: tryConvertUIUrvalToTEDUrval(props.analysSettings.urval),
          options: {
            antal_decimaler: other.decimals,
            endast_bokfort: !other.includeEjBokfordaJournaler,
            enhet: String(other.denomination),
            visa_jamforelse: other.showComparison,
            summerings_mode: other.summationMode.value,
            exkludera_personalkostnader: other.exkluderaPersonalKostnader,
            visa_summering: analys.visaSummeringar,
            expand_to_index: analys.other.expandedSumIndex,
          },
          grundurval_id: props.selectedAnalys.analys.grundurval
            ? props.selectedAnalys.analys.grundurval.id
            : undefined,
        },
        saveAnalysCallback
      );
    }
  }, [
    analysName,
    anvandsForValue,
    distribution,
    props.analysSettings,
    props.dbName,
    props.newAnalys,
    props.open,
    saveAnalys,
    props.selectedAnalys.analys.grundurval,
    props.selectedAnalys.analys.id,
    saveAnalysCallback,
    sparaForAnvandare,
  ]);

  const saveDisabled = useCallback(
    (queriesLoading: boolean): boolean => {
      const isAnalysNameEmpty = analysName === '';
      if (!props.newAnalys) {
        const origState = {
          distribution: getDistribution(
            props.analysSettings.category,
            props.userInfo?.isUtdelningBehorig ?? false
          ),
          analysNameError: undefined,
          sparaForAnvandare: [],
        };

        const currentState = {
          distribution: distribution,
          analysNameError: analysNameError,
          sparaForAnvandare: sparaForAnvandare,
        };
        const exclude = {
          anvandsFor: [],
          analysName: '',
          analysNamePristine: true,
          changedAnalysName: false,
          changedCheckbox: false,
          changedDist: false,
        };

        return (
          (equals(
            {
              ...origState,
              ...exclude,
            },
            {
              ...currentState,
              ...exclude,
            }
          ) &&
            !checkAnvandsForChanged(anvandsForValue) &&
            !checkAnalysNamnChanged(analysName)) ||
          isAnalysNameEmpty
        );
      }

      return (
        (analysNamePristine && props.newAnalys) ||
        Boolean(analysNameError) ||
        anvandsForValue.length === 0 ||
        props.saving ||
        (distribution === AnalysDistribution.SparaForAnvandare &&
          sparaForAnvandare.length === 0) ||
        isAnalysNameEmpty ||
        queriesLoading
      );
    },
    [
      analysName,
      analysNameError,
      analysNamePristine,
      checkAnalysNamnChanged,
      checkAnvandsForChanged,
      distribution,
      props.analysSettings.category,
      props.newAnalys,
      props.saving,
      props.userInfo?.isUtdelningBehorig,
      sparaForAnvandare,
      anvandsForValue,
    ]
  );

  const getOptionSelected = useCallback(
    (option: ISelectChoice<string>, value: ISelectChoice<string>) =>
      option.header === value?.header,
    []
  );

  const footerActions = useCallback(
    (queriesLoading: boolean) => {
      let disabledSaveReason: string | undefined;
      if (queriesLoading) {
        disabledSaveReason = 'Analys kontrolleras';
      } else if (analysNameError) {
        disabledSaveReason = analysNameError;
      } else if (anvandsForValue.length === 0) {
        disabledSaveReason = 'Ingen kodbehörighet att kombinera med vald';
      } else if (
        distribution === AnalysDistribution.SparaForAnvandare &&
        sparaForAnvandare.length === 0
      ) {
        disabledSaveReason =
          'Du måste välja användare som analysen ska sparas för';
      } else if (props.saving) {
        disabledSaveReason = 'Analysen sparas';
      } else if (analysName === '') {
        disabledSaveReason = 'Analysnamn måste ha ett värde';
      } else if (changedAnalysName && changedCheckbox && changedDist) {
        disabledSaveReason = 'Analysen har inte ändrats';
      }

      const cancelButton = {
        label: 'Avbryt',
        onClick: closeModal,
        buttonProps: {
          disabled: props.saving,
          disabledReason: props.saving
            ? 'Kan ej stänga medans analysen sparas'
            : undefined,
        },
      };
      const saveButton = {
        label: props.saving ? 'Sparar' : 'Spara',
        onClick: handleSaveAnalys,
        buttonProps: {
          disabled: saveDisabled(queriesLoading),
          disabledReason:
            analysNamePristine && props.newAnalys
              ? 'Analysnamn ej valt'
              : disabledSaveReason,
        },
      };
      if (props.saving) {
        return [saveButton];
      }
      return [cancelButton, saveButton];
    },
    [
      analysName,
      analysNameError,
      analysNamePristine,
      anvandsForValue,
      changedAnalysName,
      changedCheckbox,
      changedDist,
      closeModal,
      distribution,
      props.newAnalys,
      props.saving,
      handleSaveAnalys,
      saveDisabled,
      sparaForAnvandare,
    ]
  );

  const endastAnvandarens = distribution === AnalysDistribution.Egen;

  const canSaveGemensamma = props.userInfo?.isUtdelningBehorig ?? false;

  if (
    !equals(prevAnvandsFor, props.analysSettings.anvandsFor) ||
    prevCodePrivilegeAnvandsFor !==
      props.selectedAnalys.codePrivilege.anvandsFor
  ) {
    setAnvandsForValue(
      initialAnvandsFor(
        props.analysSettings.anvandsFor,
        props.selectedAnalys.codePrivilege.anvandsFor
      )
    );
    setPrevAnvandsFor(props.analysSettings.anvandsFor);

    setPrevCodePrivilegeAnvandsFor(
      props.selectedAnalys.codePrivilege.anvandsFor
    );
  }
  if (
    prevNewAnalys !== props.newAnalys ||
    !equals(prevAnalysSettings, props.analysSettings)
  ) {
    resetStates();
  }

  return (
    <StandardAnalysQuery dbname={props.dbName}>
      {(standardAnalyser, standardAnalysQueryLoading) => {
        return (
          <AnalysEgnaLokalaQuery
            dbname={props.dbName}
            endastAnvandarens={endastAnvandarens}
          >
            {(analyser, egnaLokalaQueryLoading) => {
              const savedAnalyser = analyser.concat(standardAnalyser);
              const savedAnalyserFiltered = props.newAnalys
                ? savedAnalyser
                : savedAnalyser.filter(
                    x => x.namn !== props.analysSettings.name
                  );

              const userSelectItems: Array<ISelectChoice<string>> = Object.keys(
                AnalysDistribution
              )
                .filter(key => key === 'Egen' || canSaveGemensamma)
                .map((key, index) => ({
                  header:
                    key === 'SparaForAnvandare' ? 'Spara för användare' : key,
                  value: AnalysDistribution[key as keyof typeof AnalysDistribution],
                  disabled: key !== 'Egen' && !canSaveGemensamma,
                  disabledReason:
                    key !== 'Egen' && !canSaveGemensamma
                      ? 'Endast delprogramansvariga för Ekonomi kan spara gemansamma analyser eller spara för andra användare.'
                      : undefined,
                }));

              const queriesLoading =
                standardAnalysQueryLoading || egnaLokalaQueryLoading;

              return (
                <Dialog
                  disableEscapeKeyDown={props.saving}
                  noCloseIcon={props.saving}
                  open={props.open}
                  onClose={closeModal}
                  title={props.newAnalys ? 'Spara analys' : 'Redigera analys'}
                  footerActions={footerActions(queriesLoading)}
                  onExited={exitModal}
                >
                  <EDOverlay
                    overlay={props.saving && <EDAvenyProgress />}
                    classes={{ overlay: classes.savingOverlay }}
                  >
                    <EDKeybind
                      keymap="Enter"
                      onKeyDown={
                        saveDisabled(queriesLoading)
                          ? () => {}
                          : handleSaveAnalys
                      }
                    >
                      <EDTextEdit
                        autoFocus
                        disabled={props.saving}
                        error={Boolean(analysNameError)}
                        helperText={analysNameError}
                        label="Analys namn"
                        marker={changedAnalysName}
                        onChange={(value: string) =>
                          analysNameChange(
                            value,
                            savedAnalyserFiltered.map(x =>
                              x.namn.trim().toLowerCase()
                            )
                          )
                        }
                        required
                        testId="AnalysNamn"
                        value={analysName}
                      />
                    </EDKeybind>
                    <EDFormGroup
                      label="Analysen passar ihop med kodbehörighet av typen"
                      line
                      size="medium"
                      classes={{ box: classes.checkboxContainer }}
                    >
                      <EDGrid container>
                        {ANVANDS_FOR_LIST.map((label, index) => {
                          const value = (index + 1).toString();
                          return (
                            <EDGrid key={index} item sm={3}>
                              <EDCheckbox
                                disabled={
                                  props.saving || label === 'Hela huvudboken'
                                }
                                disabledReason={
                                  label === 'Hela huvudboken'
                                    ? 'Hela huvudboken kan användas för alla analyser'
                                    : undefined
                                }
                                marker={checkCheckboxMarker(value)}
                                label={label}
                                checked={anvandsForValue.some(x => x === value)}
                                onCheck={() => anvandForCheck(value)}
                              />
                            </EDGrid>
                          );
                        })}
                      </EDGrid>
                    </EDFormGroup>
                    <EDAutoCompleteEdit
                      label="Analys distribuering"
                      notSelectedLabel="Analys distribuering"
                      value={userSelectItems.find(
                        i => i.value === distribution
                      )}
                      fullWidth
                      getOptionLabel={a => a.header}
                      marker={changedDist}
                      options={userSelectItems}
                      onChange={distributionChange}
                      disabled={props.saving}
                    />
                    {distribution === AnalysDistribution.Gemensam &&
                      urvalHasValue(props.analysSettings.urval) && (
                        <span className={classes.warningContainer}>
                          <EDIcon
                            paddingRight={1}
                            iconType="warning"
                            color="warning"
                          />
                          <EDTypography className={classes.warningColor}>
                            För gemensamma analysmallar sparas ej urvalet, men
                            summeringar och kolumner ingår. När analysen används
                            filtreras den med respektive användares
                            kodbehörighet.
                          </EDTypography>
                        </span>
                      )}
                    {distribution === AnalysDistribution.SparaForAnvandare && (
                      <EkonomiskAnalysUserSignaturesQuery
                        dbName={props.dbName}
                        anvandsFor={getAnvandsForUrvalConstants(
                          anvandsForValue
                        )}
                      >
                        {({ users }) => {
                          const userOptions = users.map(
                            (user): ISelectChoice<string> => ({
                              header: user.name,
                              value: user.id,
                            })
                          );

                          return (
                            <>
                              <EDAutoCompleteEdit<ISelectChoice<string>, true>
                                classes={{
                                  tag: classes.tag,
                                  inputRoot:
                                    sparaForAnvandare.length > 3
                                      ? classes.inputRoot
                                      : undefined,
                                }}
                                fullWidth
                                getOptionLabel={choice => choice.header}
                                getOptionSelected={getOptionSelected}
                                hideClear
                                label="Spara för användare"
                                multiple
                                notSelectedLabel="Spara för användare"
                                onChange={sparaForAnvandareChange}
                                options={userOptions}
                                renderTags={(tagValues, getTagProps) =>
                                  tagValues.map((value, index) => {
                                    return (
                                      <EDChip
                                        label={value.header}
                                        {...getTagProps({ index })}
                                        variant="outlined"
                                        key={value.value}
                                      />
                                    );
                                  })
                                }
                                value={sparaForAnvandare}
                              />
                              {urvalHasValue(props.analysSettings.urval) && (
                                <EDBox className={classes.warningContainer}>
                                  <EDIcon
                                    paddingRight={1}
                                    iconType="warning"
                                    color="warning"
                                  />
                                  <EDTypography
                                    className={classes.warningColor}
                                  >
                                    Samtliga inställningar samt eventuella urval
                                    sparas i analysen. När analysen används
                                    filtreras den med respektive användares
                                    kodbehörighet.
                                  </EDTypography>
                                </EDBox>
                              )}
                            </>
                          );
                        }}
                      </EkonomiskAnalysUserSignaturesQuery>
                    )}
                  </EDOverlay>
                </Dialog>
              );
            }}
          </AnalysEgnaLokalaQuery>
        );
      }}
    </StandardAnalysQuery>
  );
};

export default EkonomiskAnalysSaveAnalysModal;
